import { LogLevel, PublicClientApplication } from '@azure/msal-browser';

// Config object to be passed to Msal on creation
export const msalConfig = {
	auth: {
		clientId: import.meta.env.VITE_CLIENT_ID,
		authority: import.meta.env.VITE_AUTHORITY,
		redirectUri: '/', // Must be registered as a SPA redirectURI on your app registration
		postLogoutRedirectUri: '/', // Must be registered as a SPA redirectURI on your app registration
	},
	cache: {
		cacheLocation: 'localStorage',
	},
	system: {
		loggerOptions: {
			loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
				if (containsPii) {
					return;
				}

				switch (level) {
					case LogLevel.Error:
						console.error(message);
						return;
					case LogLevel.Info:
						console.info(message);
						return;
					case LogLevel.Verbose:
						console.debug(message);
						return;
					case LogLevel.Warning:
						console.warn(message);
						return;
					default:
						return;
				}
			},
			logLevel: LogLevel.Verbose,
		},
	},
};

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Scopes for the 125 platform API, or null when no resource is configured.
 *
 * A separate request because Entra will not mint one access token spanning two
 * resources: these scopes CANNOT be added to `loginRequest` below, which would
 * break depot token acquisition rather than add a permission.
 *
 * Both scopes are requested together, which is legal because they belong to one
 * resource, and matches what the depot request does with its two. Neither scope
 * distinguishes privilege — delegated scopes are consented tenant-wide rather
 * than granted per user — so privilege comes from the token's `roles` claim.
 *
 * The whole App ID URI comes from configuration because it differs by name
 * between environments (`api://125-platform-internal-testing` in test,
 * `api://125-platform-internal` in production) rather than by GUID. Renaming an
 * identifier URI in Entra replaces the default `api://<clientId>`, so the client
 * id is NOT usable here the way the depot backend's is below.
 */
const platformAppIdUri = import.meta.env.VITE_ENTRA_125_PLATFORM_APP_ID_URI;

export const platformRequest = platformAppIdUri
	? {
			scopes: [
				`${platformAppIdUri}/access_as_user`,
				`${platformAppIdUri}/access_as_admin`,
			],
		}
	: null;

// Add here scopes for id token to be used at MS Identity Platform endpoints.
export const loginRequest = {
	scopes: [
		`api://${import.meta.env.VITE_ENTRA_DEPOT_BACKEND_APP_ID}/Depot.User.All`,
		`api://${import.meta.env.VITE_ENTRA_DEPOT_BACKEND_APP_ID}/Depot.Admin.All`,
	],
};

/**
 * The interactive form of `loginRequest`. A token request cannot span resources
 * but CONSENT can, so the platform scopes ride along on the sign-in popup and
 * the platform token can then be acquired silently instead of prompting again.
 *
 * Kept separate from `loginRequest` because `extraScopesToConsent` is meaningless
 * on `acquireTokenSilent`, which is where `loginRequest` is otherwise spread.
 */
export const interactiveLoginRequest = platformRequest
	? { ...loginRequest, extraScopesToConsent: platformRequest.scopes }
	: loginRequest;

// Scopes required to read the signed-in user's profile/photo from Microsoft Graph.
// These target the Graph resource and are acquired separately from the backend API token.
export const graphRequest = {
	scopes: ['User.Read'],
};

// Add here the endpoints for MS Graph API services you would like to use.
export const graphConfig = {
	graphMeEndpoint: import.meta.env.VITE_GRAPH_ENDPOINT,
	graphPhotoEndpoint: `${import.meta.env.VITE_GRAPH_ENDPOINT}/photo/$value`,
};
